import './FamilyProfileSwitcher.scss';

import { useEffect, useState } from 'react';

import { Sanitize } from '@components/Sanitize';
import type { FamilyProfile } from '@customTypes/user';

const DESKTOP_MIN_WIDTH = 768;
const AVATAR_TONES = ['navy', 'teal', 'magenta', 'gray'] as const;

export interface FamilyProfileSwitcherProps {
  profiles: FamilyProfile[];
  activeKey: string;
  onSelect: (identifier: string) => void;
}

const profileLabel = (profile: FamilyProfile) => (profile.is_self ? `${profile.first_name} (you)` : profile.first_name);

const profileInitial = (profile: FamilyProfile) => profile.first_name.trim().charAt(0).toUpperCase();

// useViewportSizing starts at width 0, so the first paint would be the dropdown.
const useDesktopSwitcher = () => {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_MIN_WIDTH);

  useEffect(() => {
    const onResize = () => {
      const next = window.innerWidth >= DESKTOP_MIN_WIDTH;
      setIsDesktop(current => (current === next ? current : next));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isDesktop;
};

const ProfileAvatar = ({ profile, tone }: { profile: FamilyProfile; tone: (typeof AVATAR_TONES)[number] }) => (
  <span
    className={`family-profile-switcher__avatar family-profile-switcher__avatar--${tone}`}
    aria-hidden='true'
    data-openreplay-sanitize
  >
    {profileInitial(profile)}
  </span>
);

export const FamilyProfileSwitcher = ({ activeKey, onSelect, profiles }: FamilyProfileSwitcherProps) => {
  const isDesktop = useDesktopSwitcher();
  if (profiles.length <= 1) return null;

  const activeProfile = profiles.find(profile => profile.identifier === activeKey) ?? profiles[0];
  const activeTone = AVATAR_TONES[Math.max(profiles.indexOf(activeProfile), 0) % AVATAR_TONES.length];

  if (!isDesktop) {
    return (
      <div className='family-profile-switcher'>
        <label className='family-profile-switcher__label' htmlFor='family-profile-switcher'>
          Viewing
        </label>
        <div className='family-profile-switcher__control' data-openreplay-sanitize>
          <ProfileAvatar profile={activeProfile} tone={activeTone} />
          <select
            id='family-profile-switcher'
            value={activeProfile.identifier}
            onChange={event => onSelect(event.target.value)}
          >
            {profiles.map(profile => (
              <option key={profile.identifier} value={profile.identifier}>
                {profileLabel(profile)}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className='family-profile-switcher family-profile-switcher--tabs'>
      {profiles.map((profile, index) => (
        <button
          key={profile.identifier}
          type='button'
          className='family-profile-switcher__tab'
          aria-pressed={profile.identifier === activeKey}
          onClick={() => onSelect(profile.identifier)}
        >
          <ProfileAvatar profile={profile} tone={AVATAR_TONES[index % AVATAR_TONES.length]} />
          <Sanitize>{profileLabel(profile)}</Sanitize>
        </button>
      ))}
    </div>
  );
};
